import { useEffect, useRef, useState } from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { MapPin, Plane, ArrowRight, ExternalLink, Navigation } from "lucide-react";
import { hasCoords, project, mapsDirections, mapsPoint, placeLabel } from "./maps";

// Shared Google Maps JS loader (v2 API). Reuses the same auth-failure hook as Places.
if (typeof window !== "undefined" && !window.__gmapsAuthHooked) {
  window.__gmapsAuthHooked = true;
  window.gm_authFailure = () => {
    window.__gmapsAuthFailed = true;
    window.dispatchEvent(new Event("gmaps-auth-failed"));
  };
}

let mapsPromise;
const loadMaps = () => {
  if (!mapsPromise) {
    mapsPromise = (async () => {
      setOptions({ key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY, v: "weekly" });
      await Promise.all([importLibrary("maps"), importLibrary("core")]);
    })();
  }
  return mapsPromise;
};

const Pin = ({ x, y, label, tone = "dark" }) => (
  <div className="absolute z-20 -translate-x-1/2 -translate-y-1/2" style={{ left: `${x}%`, top: `${y}%` }}>
    <span className={`absolute inset-0 -m-2 rounded-full ${tone === "dark" ? "bg-[#1A1B18]/20" : "bg-[#7A4B3B]/25"} animate-ping`} />
    <span className={`relative block w-3 h-3 rounded-full ring-2 ring-white ${tone === "dark" ? "bg-[#1A1B18]" : "bg-[#7A4B3B]"}`} />
    {label && (
      <span className="absolute left-1/2 top-4 -translate-x-1/2 whitespace-nowrap text-[10px] uppercase tracking-[0.12em] text-[#1A1B18] bg-white/85 px-1.5 py-0.5 backdrop-blur-sm">{label}</span>
    )}
  </div>
);

// Flights → premium world map (image base, no tiles) + dashed great-circle-style arc.
export const FlightMap = ({ departure, arrival, depText, arrText }) => {
  const dep = hasCoords(departure) ? project(departure.lat, departure.lng) : { x: 30, y: 42 };
  const arr = hasCoords(arrival) ? project(arrival.lat, arrival.lng) : { x: 68, y: 55 };
  const cx = (dep.x + arr.x) / 2;
  const cy = Math.max(4, Math.min(dep.y, arr.y) - Math.abs(arr.x - dep.x) * 0.28);
  const apex = { x: 0.25 * dep.x + 0.5 * cx + 0.25 * arr.x, y: 0.25 * dep.y + 0.5 * cy + 0.25 * arr.y };
  const href = mapsDirections(departure || { name: depText }, arrival || { name: arrText });

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid="flight-map"
       className="group relative block w-full aspect-[3/2] md:aspect-[5/2] overflow-hidden bg-[#FAF9F6]">
      <img src="/world-map.jpg" alt="Carte du trajet aérien" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90" />
      <div className="absolute inset-0 bg-[#FAF9F6]/10" />
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        <path d={`M ${dep.x} ${dep.y} Q ${cx} ${cy} ${arr.x} ${arr.y}`} fill="none"
              stroke="#7A4B3B" strokeWidth="0.5" strokeDasharray="1.4 1.4" vectorEffect="non-scaling-stroke" opacity="0.9" />
      </svg>
      <Pin x={dep.x} y={dep.y} label={depText} tone="dark" />
      <Pin x={arr.x} y={arr.y} label={arrText} tone="accent" />
      <div className="absolute z-20 -translate-x-1/2 -translate-y-1/2" style={{ left: `${apex.x}%`, top: `${apex.y}%` }}>
        <Plane className="h-5 w-5 text-[#7A4B3B] rotate-45 drop-shadow" />
      </div>
      <span className="absolute bottom-3 right-3 z-20 flex items-center gap-1 bg-white/85 text-[#1A1B18] text-[10px] uppercase tracking-[0.15em] px-2.5 py-1.5 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
        Voir sur Google Maps <ExternalLink className="h-3 w-3" />
      </span>
    </a>
  );
};

// Transfers → stylised local mini-map (no tiles), two points + dashed route.
export const TransferMap = ({ departure, arrival, depText, arrText }) => {
  let a = { x: 26, y: 64 }, b = { x: 74, y: 36 };
  if (hasCoords(departure) && hasCoords(arrival)) {
    const minLat = Math.min(departure.lat, arrival.lat), maxLat = Math.max(departure.lat, arrival.lat);
    const minLng = Math.min(departure.lng, arrival.lng), maxLng = Math.max(departure.lng, arrival.lng);
    const dLat = maxLat - minLat || 1, dLng = maxLng - minLng || 1;
    const nx = (lng) => 18 + ((lng - minLng) / dLng) * 64;
    const ny = (lat) => 82 - ((lat - minLat) / dLat) * 64;
    a = { x: nx(departure.lng), y: ny(departure.lat) };
    b = { x: nx(arrival.lng), y: ny(arrival.lat) };
  }
  const href = mapsDirections(departure || { name: depText }, arrival || { name: arrText });
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid="transfer-map"
       className="group relative block w-full aspect-[16/7] overflow-hidden bg-[#F3EFE7]"
       style={{ backgroundImage: "radial-gradient(#e0dacd 1px, transparent 1px)", backgroundSize: "16px 16px" }}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        <path d={`M ${a.x} ${a.y} C ${(a.x + b.x) / 2} ${a.y}, ${(a.x + b.x) / 2} ${b.y}, ${b.x} ${b.y}`}
              fill="none" stroke="#7A4B3B" strokeWidth="0.6" strokeDasharray="1.6 1.6" vectorEffect="non-scaling-stroke" />
      </svg>
      <Pin x={a.x} y={a.y} label={depText} tone="dark" />
      <Pin x={b.x} y={b.y} label={arrText} tone="accent" />
      <span className="absolute bottom-3 right-3 z-20 flex items-center gap-1 bg-white/85 text-[#1A1B18] text-[10px] uppercase tracking-[0.15em] px-2.5 py-1.5 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
        Itinéraire <ExternalLink className="h-3 w-3" />
      </span>
    </a>
  );
};

// Single location (hotel / activity) → premium point panel, no tiles.
export const LocationMap = ({ place, text }) => {
  const label = placeLabel(place, text);
  const href = mapsPoint(place || { name: text });
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid="location-map"
       className="group relative block w-full h-full min-h-[220px] overflow-hidden bg-[#F3EFE7]"
       style={{ backgroundImage: "radial-gradient(#e0dacd 1px, transparent 1px)", backgroundSize: "16px 16px" }}>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="absolute w-24 h-24 rounded-full border border-[#d8cfbd]" />
        <span className="absolute w-40 h-40 rounded-full border border-[#e0d8c8]" />
        <span className="relative flex flex-col items-center">
          <MapPin className="h-8 w-8 text-[#7A4B3B]" fill="#7A4B3B" fillOpacity="0.12" />
        </span>
      </div>
      <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-[#F3EFE7] via-[#F3EFE7]/85 to-transparent">
        {label && <p className="text-sm font-medium text-[#1A1B18] truncate">{label}</p>}
        {place?.address && place.address !== label && <p className="text-xs text-[#737373] truncate">{place.address}</p>}
        <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-[#7A4B3B]">
          <Navigation className="h-3 w-3" /> Ouvrir dans Google Maps
        </span>
      </div>
    </a>
  );
};

// Compact departure → arrival row used above the maps.
export const RouteLine = ({ from, to }) => (
  <div className="flex items-center gap-3 text-lg">
    <span className="font-medium">{from || "—"}</span>
    <ArrowRight className="h-4 w-4 text-[#96836B] shrink-0" />
    <span className="font-medium">{to || "—"}</span>
  </div>
);

// Real interactive Google Map for flights & transfers.
// Departure + arrival markers, auto-fit bounds, straight route line, zoom/pan,
// click-through to Google Maps. Falls back to the premium SVG map when Google
// is unavailable (unauthorised key / no billing) or when coordinates are missing.
const ASPECT = { flight: "aspect-[4/3] sm:aspect-[3/2] md:aspect-[5/2]", transfer: "aspect-[4/3] sm:aspect-[16/9] md:aspect-[16/7]" };

export const InteractiveRouteMap = ({ departure, arrival, depText, arrText, mode = "flight" }) => {
  const ref = useRef(null);
  const dep = hasCoords(departure) ? departure : null;
  const arr = hasCoords(arrival) ? arrival : null;
  const [failed, setFailed] = useState(
    (typeof window !== "undefined" && !!window.__gmapsAuthFailed) || (!dep && !arr)
  );

  useEffect(() => {
    if (!dep && !arr) return;
    let cancelled = false;
    const onFail = () => setFailed(true);
    window.addEventListener("gmaps-auth-failed", onFail);

    loadMaps()
      .then(() => {
        if (cancelled || !ref.current) return;
        const g = window.google.maps;
        const map = new g.Map(ref.current, {
          disableDefaultUI: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          gestureHandling: "cooperative",
          clickableIcons: false,
        });

        const points = [dep, arr].filter(Boolean);
        const bounds = new g.LatLngBounds();

        points.forEach((p, i) => {
          new g.Marker({
            map,
            position: { lat: p.lat, lng: p.lng },
            title: i === 0 ? depText : arrText,
            icon: {
              path: g.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: i === 0 ? "#1A1B18" : "#7A4B3B",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2.5,
            },
          });
          bounds.extend({ lat: p.lat, lng: p.lng });
        });

        if (dep && arr) {
          const dashed = mode === "flight";
          new g.Polyline({
            map,
            path: [
              { lat: dep.lat, lng: dep.lng },
              { lat: arr.lat, lng: arr.lng },
            ],
            geodesic: true,
            strokeColor: "#7A4B3B",
            strokeOpacity: dashed ? 0 : 0.9,
            strokeWeight: 2.5,
            icons: dashed
              ? [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, strokeColor: "#7A4B3B", scale: 3 }, offset: "0", repeat: "14px" }]
              : undefined,
          });
          map.fitBounds(bounds, (ref.current?.offsetWidth || 0) < 480 ? { top: 26, right: 22, bottom: 42, left: 22 } : 64);
        } else {
          map.setCenter({ lat: points[0].lat, lng: points[0].lng });
          map.setZoom(13);
        }
      })
      .catch(() => setFailed(true));

    return () => {
      cancelled = true;
      window.removeEventListener("gmaps-auth-failed", onFail);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failed) {
    return mode === "flight" ? (
      <FlightMap departure={departure} arrival={arrival} depText={depText} arrText={arrText} />
    ) : (
      <TransferMap departure={departure} arrival={arrival} depText={depText} arrText={arrText} />
    );
  }

  const href = mapsDirections(departure || { name: depText }, arrival || { name: arrText });
  return (
    <div className={`relative w-full ${ASPECT[mode] || ASPECT.flight}`}>
      <div ref={ref} data-testid={`${mode}-interactive-map`} className="absolute inset-0 w-full h-full" />
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${mode}-map-link`}
        className="absolute bottom-9 right-2 md:bottom-3 md:right-3 z-10 flex items-center gap-1 bg-white/90 text-[#1A1B18] text-[10px] uppercase tracking-[0.15em] px-2.5 py-1.5 backdrop-blur-sm shadow-sm hover:bg-white transition-colors"
      >
        {mode === "flight" ? "Voir sur Google Maps" : "Itinéraire"} <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
};
